import { useEffect, useRef, useState } from "react";
import { categories, articles } from "@/mocks/guaData";

const catLabel = Object.fromEntries(categories.map((c) => [c.id, c.label]));

const tabs = [{ id: "all", label: "全部推荐" }, ...categories.map(({ id, label }) => ({ id, label }))];

export default function Channel() {
  const [active, setActive] = useState("all");
  const [shown, setShown] = useState(6);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = active === "all" ? articles : articles.filter((a) => a.cat === active);

  useEffect(() => {
    setShown(6);
  }, [active]);

  const pickCategory = (id: string) => {
    setActive(id);
    requestAnimationFrame(() => listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  const visible = filtered.slice(0, shown);

  return (
    <section id="channel" className="scroll-mt-28 bg-paper-50">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="mb-1 text-xs font-semibold tracking-[0.2em] text-primary-600">CHANNELS</div>
            <h2 className="font-serif text-2xl font-extrabold tracking-tight text-stone-900 md:text-3xl">热门栏目 · 想看哪种瓜</h2>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-stone-500">六个栏目各有分工：追踪最新、拆解名场面、复盘争议，也替你把「瓜」验一验真假。</p>
        </div>

        {/* 栏目卡片 */}
        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => pickCategory(c.id)}
              className="group cursor-pointer overflow-hidden rounded-lg border border-paper-200 bg-white text-left transition hover:-translate-y-1 hover:border-primary-200"
            >
              <div className="relative h-24 overflow-hidden">
                <img
                  src={c.image}
                  alt={c.label}
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <span className="absolute bottom-2 left-2.5 flex items-center gap-1.5 text-xs font-semibold text-white">
                  <i className={`${c.icon} text-base`} />
                  {c.label}
                </span>
              </div>
              <div className="p-2.5">
                <p className="line-clamp-2 text-[11px] leading-snug text-stone-500">{c.desc}</p>
                <p className="mt-1.5 text-[11px] font-medium text-accent-600">{c.total}</p>
              </div>
            </button>
          ))}
        </div>

        {/* 文章列表 */}
        <div ref={listRef} className="mt-10 scroll-mt-28">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-paper-200 pb-3">
            <div className="flex flex-wrap items-center gap-1 rounded-full bg-paper-100 p-1">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActive(t.id)}
                  className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition ${
                    active === t.id
                      ? "bg-primary-500 font-medium text-white shadow-sm"
                      : "text-stone-600 hover:bg-paper-200 hover:text-primary-700"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <span className="text-sm text-stone-500">共 {filtered.length} 篇 · 摘要更新至最近</span>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4">
            {visible.map((a) => (
              <article
                key={a.id}
                className="group grid grid-cols-1 gap-4 rounded-lg border border-paper-200 bg-white p-4 transition hover:border-primary-200 hover:bg-white sm:grid-cols-[1fr_auto]"
              >
                <div className="min-w-0">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-primary-50 px-2 py-0.5 text-[11px] font-medium text-primary-700">
                      {catLabel[a.cat] || a.cat}
                    </span>
                    <span className="rounded-full bg-accent-100 px-2 py-0.5 text-[11px] font-medium text-accent-800">#{a.tag}</span>
                  </div>
                  <h3 className="font-serif text-base font-bold leading-snug text-stone-900 transition group-hover:text-primary-700 md:text-lg">
                    {a.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-stone-600">{a.summary}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-stone-400">
                    <span className="inline-flex items-center gap-1"><i className="ri-user-3-line text-stone-400" />{a.author}</span>
                    <span className="inline-flex items-center gap-1"><i className="ri-calendar-line" />{a.date}</span>
                    <span className="inline-flex items-center gap-1"><i className="ri-time-line" />{a.read} 分钟</span>
                    <span className="inline-flex items-center gap-1 text-primary-500"><i className="ri-fire-line" />{a.heat}</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  className="inline-flex items-center justify-center gap-1.5 self-end whitespace-nowrap rounded-md border border-primary-200 bg-primary-50 px-3 py-1.5 text-xs font-medium text-primary-700 transition hover:bg-primary-500 hover:text-white sm:self-center"
                >
                  了解事件
                  <i className="ri-arrow-right-up-line" />
                </button>
              </article>
            ))}
          </div>

          {shown < filtered.length && (
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={() => setShown((s) => s + 4)}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-paper-100 px-5 py-2.5 text-sm font-medium text-stone-700 transition hover:bg-primary-100 hover:text-primary-700"
              >
                <i className="ri-add-line" />
                加载更多（还有 {filtered.length - shown} 篇）
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}